import axios from 'axios';

// ── Label → color map (mirrors MultiMap.js LABEL_COLOR_MAP) ──────────────────
const LABEL_COLOR_MAP = {
  'pavement damage (severe)': '#DC3C3C',
  'potholes': '#DC3C3C',
  'pothole': '#DC3C3C',
  'settlement': '#DC3C3C',
  'rutting': '#DC3C3C',
  'shoulder edge drop': '#DC3C3C',
  'damaged sign boards / sign structures': '#DC3C3C',
  'damaged sign boards': '#DC3C3C',
  'damaged highway lights': '#DC3C3C',
  'non-functional highway lights': '#DC3C3C',
  'missing assets (street lights)': '#DC3C3C',
  'damaged crash barriers': '#DC3C3C',
  'damaged metal beam crash barrier': '#DC3C3C',
  'damaged pedestrian guard rail': '#DC3C3C',
  'damaged kerb': '#DC3C3C',
  'damaged drain cover slabs': '#DC3C3C',
  'missing drain cover slabs': '#DC3C3C',
  'damaged blinkers': '#DC3C3C',
  'damaged attenuators': '#DC3C3C',
  'damaged delineators': '#DC3C3C',
  'damaged anti-glare': '#DC3C3C',
  'damaged hazard markers': '#DC3C3C',
  'reduced visibility due to overgrowth': '#DC3C3C',
  'damaged rumble strips': '#DC3C3C',
  'faded pavement marking': '#DC3C3C',
  'pavement marking - poor visibility (night)': '#DC3C3C',
  'work zone - inadequate barricading': '#DC3C3C',
  'work zone - poor diversion arrangement / condition': '#DC3C3C',
  'missing assets (guard rails)': '#DC3C3C',
  'median separator damaged': '#DC3C3C',
  'unauthorized median opening': '#DC3C3C',
  'stripping / delamination': '#E87A00',
  'cracks': '#E87A00',
  'crack': '#E87A00',
  'cracking': '#E87A00',
  'water stagnation': '#E87A00',
  'shoulder unevenness': '#E87A00',
  'shoulder rain cut': '#E87A00',
  'signage - poor visibility (day)': '#E87A00',
  'signage - poor visibility (night)': '#E87A00',
  'damaged footpath tiles / paver blocks': '#E87A00',
  'damaged footpath': '#E87A00',
  'damaged road studs': '#E87A00',
  'bus bay - damaged shelters': '#E87A00',
  'truck lay by - damaged shelters': '#E87A00',
  'missing plants / irregular gaps (median)': '#E87A00',
  'deteriorated or damaged plants (median)': '#E87A00',
  'unauthorized hoardings': '#E87A00',
  'pavement marking - poor visibility (day)': '#E87A00',
  'work zone - inadequate signboard visibility': '#E87A00',
  'faded painting concrete crash barrier': '#E87A00',
  'barriers - faded painting guard rails': '#E87A00',
  'faded kerb painting': '#E87A00',
  'median separator paint faded': '#E87A00',
  'general encroachment': '#E87A00',
  'unsealed roads': '#C8C800',
  'pavement joint': '#C8C800',
  'road studs - poor visibility (day)': '#C8C800',
  'road studs - poor visibility (night)': '#C8C800',
  'bus bay - faded markings': '#C8C800',
  'bus bay - damaged signages': '#C8C800',
  'truck lay by - faded markings': '#C8C800',
  'truck lay by - damaged signages': '#C8C800',
  'manhole': '#C8C800',
  'illegal parking': '#C8C800',
  'shoulder - vegetation growth': '#C8C800',
  'excessive plantation growth': '#C8C800',
  'unauthorized advertisement board': '#C8C800',
  'cleanliness - litter': '#C8C800',
  'cleanliness - debris': '#C8C800',
};

const _normLabel = (label) =>
  label.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

const _getLabelColor = (rawLabel) => {
  const norm = _normLabel(rawLabel);
  if (LABEL_COLOR_MAP[norm]) return LABEL_COLOR_MAP[norm];
  for (const [key, color] of Object.entries(LABEL_COLOR_MAP)) {
    if (norm.includes(key) || key.includes(norm)) return color;
  }
  return null;
};

// Mirrors getMarkerColor in MultiMap.js
const _getFrameColor = (defectState, inferenceInfo, labelFilter) => {
  const state = (defectState || '').toLowerCase();
  if (state === 'high')   return '#DC3C3C';
  if (state === 'medium') return '#E87A00';
  if (state === 'low')    return '#C8C800';
  const filteredInfo = labelFilter
    ? (inferenceInfo || []).filter(info => labelFilter(info.label || ''))
    : inferenceInfo;
  if (filteredInfo && filteredInfo.length > 0) {
    const COLOR_PRIORITY = { '#DC3C3C': 3, '#E87A00': 2, '#C8C800': 1 };
    const colorCounts = {};
    filteredInfo.forEach(info => {
      const color = _getLabelColor(info.label || '');
      if (color) colorCounts[color] = (colorCounts[color] || 0) + 1;
    });
    const entries = Object.entries(colorCounts);
    if (entries.length > 0) {
      return entries.sort(
        (a, b) => b[1] - a[1] || (COLOR_PRIORITY[b[0]] || 0) - (COLOR_PRIORITY[a[0]] || 0)
      )[0][0];
    }
    return '#C8C800';
  }
  return '#00BB00';
};

// Haversine distance in km
const _calcDist = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ── Core calculation (same logic as kmBySeverity in MultiMap.js) ─────────────
export const computeKmBySeverity = (inferenceList, labelFilter) => {
  const totals = { high: 0, medium: 0, low: 0, noDefect: 0 };

  const classifySegment = (frames) => {
    const counts = { high: 0, medium: 0, low: 0, noDefect: 0 };
    frames.forEach(frame => {
      const color = _getFrameColor(frame.defect_state, frame.inference_info, labelFilter);
      if      (color === '#DC3C3C') counts.high++;
      else if (color === '#E87A00') counts.medium++;
      else if (color === '#C8C800') counts.low++;
      else                          counts.noDefect++;
    });
    return ['high', 'medium', 'low', 'noDefect'].sort((a, b) => counts[b] - counts[a])[0];
  };

  const seenRoadIds = new Set();

  inferenceList.forEach((inference) => {
    const roadId = inference.data?.road_id || inference.uid;
    if (seenRoadIds.has(roadId)) return;
    seenRoadIds.add(roadId);

    const roadLength = typeof inference.data?.road_length === 'number' ? inference.data.road_length : 0;
    if (roadLength <= 0) return;

    const frames = (inference.data?.frame_list_data || []).filter(
      f => f.latitude && f.longitude && !(f.latitude === 0 && f.longitude === 0)
    );
    if (frames.length === 0) return;

    const segmentCounts = { high: 0, medium: 0, low: 0, noDefect: 0 };
    let segmentFrames = [];
    let segmentKm = 0;
    let totalSegments = 0;

    for (let i = 0; i < frames.length; i++) {
      segmentFrames.push(frames[i]);
      if (i < frames.length - 1) {
        segmentKm += _calcDist(frames[i].latitude, frames[i].longitude, frames[i + 1].latitude, frames[i + 1].longitude);
        if (segmentKm >= 0.1) {
          segmentCounts[classifySegment(segmentFrames)]++;
          totalSegments++;
          segmentFrames = [];
          segmentKm = 0;
        }
      }
    }
    if (segmentFrames.length > 0) {
      segmentCounts[classifySegment(segmentFrames)]++;
      totalSegments++;
    }
    if (totalSegments === 0) return;

    totals.high     += (segmentCounts.high     / totalSegments) * roadLength;
    totals.medium   += (segmentCounts.medium   / totalSegments) * roadLength;
    totals.low      += (segmentCounts.low      / totalSegments) * roadLength;
    totals.noDefect += (segmentCounts.noDefect / totalSegments) * roadLength;
  });

  return totals;
};

// ── Same segment logic, but grouped by survey date (YYYY-MM-DD) ──────────────
// Used by FinalReportDashboard line chart.
// Returns { 'YYYY-MM-DD': { high, medium, low, noDefect }, ... }
export const computeKmBySeverityByDate = (inferenceList, labelFilter) => {
  const byDate = {};

  const toDateKey = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v).slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const classifySegment = (frames) => {
    const counts = { high: 0, medium: 0, low: 0, noDefect: 0 };
    frames.forEach(frame => {
      const color = _getFrameColor(frame.defect_state, frame.inference_info, labelFilter);
      if      (color === '#DC3C3C') counts.high++;
      else if (color === '#E87A00') counts.medium++;
      else if (color === '#C8C800') counts.low++;
      else                          counts.noDefect++;
    });
    return ['high', 'medium', 'low', 'noDefect'].sort((a, b) => counts[b] - counts[a])[0];
  };

  const seenRoadIds = new Set();

  inferenceList.forEach((inference) => {
    const roadId = inference.data?.road_id || inference.uid;
    if (seenRoadIds.has(roadId)) return;
    seenRoadIds.add(roadId);

    const dateKey = toDateKey(inference.created?.['$date'] || inference.created);
    if (!dateKey) return;

    const roadLength = typeof inference.data?.road_length === 'number' ? inference.data.road_length : 0;
    if (roadLength <= 0) return;

    const frames = (inference.data?.frame_list_data || []).filter(
      f => f.latitude && f.longitude && !(f.latitude === 0 && f.longitude === 0)
    );
    if (frames.length === 0) return;

    const segmentCounts = { high: 0, medium: 0, low: 0, noDefect: 0 };
    let segmentFrames = [];
    let segmentKm = 0;
    let totalSegments = 0;

    for (let i = 0; i < frames.length; i++) {
      segmentFrames.push(frames[i]);
      if (i < frames.length - 1) {
        segmentKm += _calcDist(frames[i].latitude, frames[i].longitude, frames[i + 1].latitude, frames[i + 1].longitude);
        if (segmentKm >= 0.1) {
          segmentCounts[classifySegment(segmentFrames)]++;
          totalSegments++;
          segmentFrames = [];
          segmentKm = 0;
        }
      }
    }
    if (segmentFrames.length > 0) {
      segmentCounts[classifySegment(segmentFrames)]++;
      totalSegments++;
    }
    if (totalSegments === 0) return;

    if (!byDate[dateKey]) byDate[dateKey] = { high: 0, medium: 0, low: 0, noDefect: 0 };
    byDate[dateKey].high     += (segmentCounts.high     / totalSegments) * roadLength;
    byDate[dateKey].medium   += (segmentCounts.medium   / totalSegments) * roadLength;
    byDate[dateKey].low      += (segmentCounts.low      / totalSegments) * roadLength;
    byDate[dateKey].noDefect += (segmentCounts.noDefect / totalSegments) * roadLength;
  });

  return byDate;
};

// ── Per-road condition map (same segment-mode logic as MultiMap) ──────────────
// Returns { [roadId]: { condition: 'Poor'|'Fair'|'Good', high, medium, low, noDefect, roadLength } }
// condition is derived from the dominant segment type (mode), mirroring MultiMap segments.
export const computeRoadConditionByRoad = (inferenceList) => {
  const byRoad = {};

  const classifySegment = (frames) => {
    const counts = { high: 0, medium: 0, low: 0, noDefect: 0 };
    frames.forEach(frame => {
      const color = _getFrameColor(frame.defect_state, frame.inference_info);
      if      (color === '#DC3C3C') counts.high++;
      else if (color === '#E87A00') counts.medium++;
      else if (color === '#C8C800') counts.low++;
      else                          counts.noDefect++;
    });
    return ['high', 'medium', 'low', 'noDefect'].sort((a, b) => counts[b] - counts[a])[0];
  };

  const seenRoadIds = new Set();

  inferenceList.forEach((inference) => {
    const roadId = inference.data?.road_id || inference.uid;
    if (seenRoadIds.has(roadId)) return;
    seenRoadIds.add(roadId);

    const roadLength = typeof inference.data?.road_length === 'number' ? inference.data.road_length : 0;
    if (roadLength <= 0) return;

    const frames = (inference.data?.frame_list_data || []).filter(
      f => f.latitude && f.longitude && !(f.latitude === 0 && f.longitude === 0)
    );
    if (frames.length === 0) return;

    const segmentCounts = { high: 0, medium: 0, low: 0, noDefect: 0 };
    let segmentFrames = [];
    let segmentKm = 0;
    let totalSegments = 0;

    for (let i = 0; i < frames.length; i++) {
      segmentFrames.push(frames[i]);
      if (i < frames.length - 1) {
        segmentKm += _calcDist(frames[i].latitude, frames[i].longitude, frames[i + 1].latitude, frames[i + 1].longitude);
        if (segmentKm >= 0.1) {
          segmentCounts[classifySegment(segmentFrames)]++;
          totalSegments++;
          segmentFrames = [];
          segmentKm = 0;
        }
      }
    }
    if (segmentFrames.length > 0) {
      segmentCounts[classifySegment(segmentFrames)]++;
      totalSegments++;
    }
    if (totalSegments === 0) return;

    const dominant = ['high', 'medium', 'low', 'noDefect'].sort(
      (a, b) => segmentCounts[b] - segmentCounts[a]
    )[0];
    const condition = dominant === 'high' ? 'Poor' : dominant === 'medium' ? 'Fair' : 'Good';

    byRoad[roadId] = {
      condition,
      high:      (segmentCounts.high     / totalSegments) * roadLength,
      medium:    (segmentCounts.medium   / totalSegments) * roadLength,
      low:       (segmentCounts.low      / totalSegments) * roadLength,
      noDefect:  (segmentCounts.noDefect / totalSegments) * roadLength,
      roadLength,
    };
  });

  return byRoad;
};

// ── Per-user cache keying ─────────────────────────────────────────────────────
// In-memory caches must be bucketed by user email so a second login on the
// same tab never reads the previous user's values.
const _getUserEmail = () => {
  try {
    return JSON.parse(window.localStorage.getItem('user'))?.email || 'default';
  } catch {
    return 'default';
  }
};

// In-memory + localStorage cache for per-road condition map (bucketed by email)
const _roadConditionMemCache = {};

export const cacheRoadConditionByRoad = (allInferences) => {
  const byRoad = computeRoadConditionByRoad(allInferences);
  if (Object.keys(byRoad).length === 0) return byRoad;
  const userEmail = _getUserEmail();
  _roadConditionMemCache[userEmail] = { data: byRoad, ts: Date.now() };
  try {
    localStorage.setItem(
      `gis_road_condition_v1_${userEmail}`,
      JSON.stringify({ payload: byRoad, ts: Date.now() })
    );
  } catch {}
  return byRoad;
};

export const getRoadConditionByRoad = () => {
  const userEmail = _getUserEmail();
  // 1. In-memory cache for this user
  const bucket = _roadConditionMemCache[userEmail];
  if (bucket?.data && Date.now() - bucket.ts < SEVERITY_CACHE_TTL) {
    return bucket.data;
  }
  // 2. localStorage cache
  try {
    const raw = localStorage.getItem(`gis_road_condition_v1_${userEmail}`);
    if (raw) {
      const { payload, ts } = JSON.parse(raw);
      if (payload && Object.keys(payload).length > 0 && Date.now() - ts < SEVERITY_CACHE_TTL) {
        _roadConditionMemCache[userEmail] = { data: payload, ts: Date.now() };
        return payload;
      }
    }
  } catch {}
  return null;
};

// ── Fetch + compute + cache ───────────────────────────────────────────────────
// Fetches all project inferences and computes severity km.
// Saves result to localStorage keyed by user email.
// Returns the computed totals.
// Module-level in-memory cache — bucketed by email so switching logins on the
// same tab does not leak the previous user's data.
const _severityMemCache = {};
const SEVERITY_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

// Called by MultiMap after it has already fetched all inferences — avoids
// the dashboard having to re-fetch the exact same data.
// Also caches per-road condition map (for Report page Road Condition Overview).
export const cacheSeverityKmFromInferences = (allInferences, labelFilter) => {
  const totals = computeKmBySeverity(allInferences, labelFilter);
  const hasNonZero = totals.high > 0 || totals.medium > 0 || totals.low > 0 || totals.noDefect > 0;
  if (!hasNonZero) return totals;

  // Filtered totals must never be written to the shared unfiltered cache key.
  if (labelFilter) return totals;

  const userEmail = _getUserEmail();
  _severityMemCache[userEmail] = { data: totals, ts: Date.now() };
  try {
    localStorage.setItem(
      `gis_severity_km_v2_${userEmail}`,
      JSON.stringify({ payload: totals, ts: Date.now() })
    );
  } catch {}

  // Also cache per-road condition map so Report page can use it
  cacheRoadConditionByRoad(allInferences);

  return totals;
};

export const fetchAndComputeSeverityKm = async (labelFilter) => {
  const userEmail = _getUserEmail();
  const cacheKey  = `gis_severity_km_v2_${userEmail}`;

  // 1. Check in-memory cache first (instant, survives navigation). Bucketed
  //    by email so a fresh login doesn't read the previous user's entry.
  const bucket = _severityMemCache[userEmail];
  if (bucket?.data && Date.now() - bucket.ts < SEVERITY_CACHE_TTL) {
    return bucket.data;
  }

  // 2. Check localStorage cache with TTL
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const { payload, ts } = JSON.parse(raw);
      const hasNonZero = payload.high > 0 || payload.medium > 0 || payload.low > 0 || payload.noDefect > 0;
      if (hasNonZero && Date.now() - ts < SEVERITY_CACHE_TTL) {
        _severityMemCache[userEmail] = { data: payload, ts: Date.now() };
        return payload;
      }
      // Expired or all-zero cache — remove and re-fetch
      localStorage.removeItem(cacheKey);
    }
  } catch {}

  // Fetch all project IDs
  const listRes = await axios.get(`${process.env.REACT_APP_BASE_URL}project/get_list`);
  const inferences = listRes.data.result.inferences || [];
  const projectIds = inferences.map(inf => inf.uid || inf['$oid']).filter(Boolean);
  if (projectIds.length === 0) return null;

  // Fetch inference data in batches of 10
  const BATCH_SIZE = 10;
  let allInferences = [];
  for (let i = 0; i < projectIds.length; i += BATCH_SIZE) {
    try {
      const batch = projectIds.slice(i, i + BATCH_SIZE);
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}project/get_project_by_id`,
        { project_uids: batch },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.data.result.inferences) allInferences = allInferences.concat(res.data.result.inferences);
    } catch {}
  }

  const totals = computeKmBySeverity(allInferences, labelFilter);
  const hasNonZero = totals.high > 0 || totals.medium > 0 || totals.low > 0 || totals.noDefect > 0;

  // Only cache non-zero results when no filter is applied
  if (hasNonZero && !labelFilter) {
    _severityMemCache[userEmail] = { data: totals, ts: Date.now() };
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ payload: totals, ts: Date.now() }));
    } catch {}
    cacheRoadConditionByRoad(allInferences);
  }

  return totals;
};

// ── Per-date variant — same fetch, groups result by survey date ───────────────
// Returns { 'YYYY-MM-DD': { high, medium, low, noDefect }, ... }
// Used by FinalReportDashboard line chart (Good/Fair/Poor km per survey date).
// Bucketed by email — see _severityMemCache for rationale.
const _severityByDateMemCache = {};

export const fetchAndComputeSeverityKmByDate = async (labelFilter) => {
  const userEmail = _getUserEmail();
  const cacheKey  = `gis_severity_km_bydate_v1_${userEmail}`;

  const bucket = _severityByDateMemCache[userEmail];
  if (bucket?.data && Date.now() - bucket.ts < SEVERITY_CACHE_TTL) {
    return bucket.data;
  }

  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const { payload, ts } = JSON.parse(raw);
      if (payload && Object.keys(payload).length > 0 && Date.now() - ts < SEVERITY_CACHE_TTL) {
        _severityByDateMemCache[userEmail] = { data: payload, ts: Date.now() };
        return payload;
      }
      localStorage.removeItem(cacheKey);
    }
  } catch {}

  const listRes = await axios.get(`${process.env.REACT_APP_BASE_URL}project/get_list`);
  const inferences = listRes.data.result.inferences || [];
  const projectIds = inferences.map(inf => inf.uid || inf['$oid']).filter(Boolean);
  if (projectIds.length === 0) return {};

  const BATCH_SIZE = 10;
  let allInferences = [];
  for (let i = 0; i < projectIds.length; i += BATCH_SIZE) {
    try {
      const batch = projectIds.slice(i, i + BATCH_SIZE);
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}project/get_project_by_id`,
        { project_uids: batch },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.data.result.inferences) allInferences = allInferences.concat(res.data.result.inferences);
    } catch {}
  }

  const byDate = computeKmBySeverityByDate(allInferences, labelFilter);
  if (Object.keys(byDate).length > 0 && !labelFilter) {
    _severityByDateMemCache[userEmail] = { data: byDate, ts: Date.now() };
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ payload: byDate, ts: Date.now() }));
    } catch {}
  }

  return byDate;
};

// Call on logout so the next login on the same tab never sees the previous
// user's in-memory or persisted severity/condition data.
export const clearAllSeverityCaches = () => {
  Object.keys(_severityMemCache).forEach(k => delete _severityMemCache[k]);
  Object.keys(_severityByDateMemCache).forEach(k => delete _severityByDateMemCache[k]);
  Object.keys(_roadConditionMemCache).forEach(k => delete _roadConditionMemCache[k]);
  try {
    Object.keys(localStorage)
      .filter(k =>
        k.startsWith('gis_severity_km_') ||
        k.startsWith('gis_road_condition_')
      )
      .forEach(k => localStorage.removeItem(k));
  } catch {}
};
